import { useCallback } from 'react';
import useAccountData from 'src/hooks/useAccountData';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { UIMarket } from '@velocity-exchange/common';
import useDriftClient from 'src/hooks/useDriftClient';
import usePrePreppedTxStore from 'src/stores/useTxDataStore';
import { TradeFormDetails } from 'src/components/TradeConfirmationModal/types';
import useModal from 'src/components/Modals/hooks/useModal';
import { useOpenPerpOrder } from 'src/hooks/trade/perp/useOpenPerpOrder';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';

interface Params {
	selectedMarket: UIMarket;
	tradeFormDetails: TradeFormDetails;
	setIsSubmitting: (isSubmitting: boolean) => void;
}

export default function usePerpTradeConfirmationModalHandlers({
	selectedMarket,
	tradeFormDetails,
	setIsSubmitting,
}: Params) {
	const user = useAccountData()?.client;
	const driftClient = useDriftClient();
	const set = useDriftStore((s) => s.set);
	const prePreparedTxs =
		usePrePreppedTxStore((s) => s.preppedPerpTradeformTxs) ?? undefined;
	const { captureEvent } = usePostHogCapture();
	const { hide } = useModal();

	const { orderType, baseSizeStringValue, quoteSizeStringValue } =
		tradeFormDetails;

	const { marketIndex } = selectedMarket;

	const openPerpOrder = useOpenPerpOrder();

	// Calculate display leverage
	const displayLeverage = useGetDisplayLeverageForMarket(marketIndex);

	const handleConfirmation = useCallback(async () => {
		if (!driftClient) return;

		const perpMarketAccount = driftClient.getPerpMarketAccount(marketIndex);

		if (!perpMarketAccount) return;

		setIsSubmitting(true);

		try {
			const isTxnSent = await openPerpOrder({
				...tradeFormDetails,
				displayLeverage,
				isolatedPositionDeposit: tradeFormDetails.isolatedPositionDeposit,
				targetMarketIndex: marketIndex,
				perpMarketAccount: perpMarketAccount,
			});

			if (isTxnSent) {
				hide();
			}
		} finally {
			setIsSubmitting(false);
		}
	}, [
		driftClient,
		marketIndex,
		set,
		user,
		captureEvent,
		baseSizeStringValue,
		quoteSizeStringValue,
		selectedMarket.market.symbol,
		orderType,
		tradeFormDetails,
		displayLeverage,
		prePreparedTxs,
		hide,
		setIsSubmitting,
		openPerpOrder,
	]);

	return {
		handleClose: hide,
		handleConfirmation,
	};
}
